// MachineDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
    ArrowLeft, 
    Calendar, 
    Cpu, 
    Clock, 
    MapPin, 
    Building, 
    Wrench, 
    Check,
    Info,
    Download,
    Share2,
    Heart,
    Star,
    Users,
    Award,
    Shield
} from "lucide-react";
import Navbar from "./Navbar";

const MACHINES = [
    {
        id: "M-3DP-01",
        name: "Industrial FDM 3D Printer",
        category: "3D Printing",
        make: "Stratasys F770",
        centre: "APIC Visakhapatnam",
        rate: 850,
        status: "Available",
        description: "High-precision FDM 3D printer with large build volume. Ideal for prototyping and production parts.",
        specs: ["Build Volume: 300x300x300mm", "Layer Resolution: 50 microns", "Material: PLA, ABS, PETG"],
        features: ["Heated Bed", "Auto-leveling", "Enclosed Chamber", "Filament Sensor"],
        rating: 4.8,
        reviews: 127,
    },
    {
        id: "M-CNC-05",
        name: "5-Axis CNC Machining Centre",
        category: "CNC",
        make: "DMG Mori DMU 50",
        centre: "APIC Vijayawada",
        rate: 2400,
        status: "Running",
        description: "Advanced 5-axis machining center for complex geometries. Perfect for aerospace and automotive parts.",
        specs: ["Travel: 500x450x400mm", "Spindle Speed: 18,000 RPM", "Tool Magazine: 30 tools"],
        features: ["5-Axis Simultaneous", "Coolant System", "Chip Conveyor", "Tool Probe"],
        rating: 4.9,
        reviews: 89,
    },
    {
        id: "M-LSR-02",
        name: "Fiber Laser Cutter 3kW",
        category: "Laser Cutting",
        make: "Trumpf TruLaser 3030",
        centre: "APIC Tirupati",
        rate: 1600,
        status: "Available",
        description: "High-power fiber laser cutter for precision cutting of metals up to 25mm thickness.",
        specs: ["Cutting Area: 3000x1500mm", "Max Thickness: 25mm", "Cutting Speed: 40m/min"],
        features: ["Auto Focus", "Piercing Control", "Sensors", "Gas Optimization"],
        rating: 4.7,
        reviews: 56,
    },
    {
        id: "M-PCB-03",
        name: "PCB Prototyping Mill",
        category: "PCB",
        make: "LPKF ProtoMat S104",
        centre: "APIC Kurnool",
        rate: 1100,
        status: "Available",
        description: "Precision PCB prototyping mill for rapid circuit board development.",
        specs: ["Working Area: 300x200mm", "Resolution: 1 micron", "Spindle Speed: 60,000 RPM"],
        features: ["Auto Tool Change", "Vision System", "Vacuum Table", "Dust Extraction"],
        rating: 4.6,
        reviews: 43,
    },
    {
        id: "M-MLD-01",
        name: "Injection Moulding Unit",
        category: "Moulding",
        make: "Arburg Allrounder 270",
        centre: "APIC Guntur",
        rate: 3200,
        status: "Maintenance",
        description: "Industrial injection molding machine for high-volume production of plastic parts.",
        specs: ["Clamping Force: 1000kN", "Shot Size: 50g", "Cycle Time: 3 seconds"],
        features: ["Hydraulic Drive", "Mold Heating", "Automation Ready", "Energy Efficient"],
        rating: 4.5,
        reviews: 34,
    },
    {
        id: "M-ROB-04",
        name: "6-Axis Robotic Arm Cell",
        category: "Robotics",
        make: "KUKA KR 10 R1100",
        centre: "APIC Nellore",
        rate: 1900,
        status: "Running",
        description: "Versatile 6-axis robotic arm for welding, assembly, and material handling.",
        specs: ["Reach: 1100mm", "Payload: 10kg", "Repeatability: ±0.05mm"],
        features: ["Teach Pendant", "Safety System", "Service Robot", "HMI Interface"],
        rating: 4.9,
        reviews: 72,
    },
];

export default function MachineDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [machine, setMachine] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Simulate API call
        setTimeout(() => {
            const found = MACHINES.find(m => m.id === id);
            setMachine(found || null);
            setLoading(false);
        }, 500);
    }, [id]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-slate-500 font-medium">Loading machine details...</p>
                    </div>
                </div>
            </>
        );
    }

    if (!machine) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center">
                    <div className="text-center max-w-md mx-4">
                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Info className="h-10 w-10 text-slate-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-800">Machine Not Found</h2>
                        <p className="mt-2 text-slate-500">The machine you're looking for doesn't exist or has been removed.</p>
                        <button 
                            onClick={() => navigate("/machineMarketplace")}
                            className="mt-6 inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-xl hover:bg-indigo-700 transition"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Marketplace
                        </button>
                    </div>
                </div>
            </>
        );
    }

    const StatusIcon = machine.status === "Available" ? Check : machine.status === "Running" ? Clock : Wrench;
    const statusColors = {
        Available: "bg-emerald-100 text-emerald-800 border-emerald-200",
        Running: "bg-blue-100 text-blue-800 border-blue-200",
        Maintenance: "bg-amber-100 text-amber-800 border-amber-200",
    };

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                    {/* Back Button */}
                    <button 
                        onClick={() => navigate("/machineMarketplace")}
                        className="group inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 font-medium mb-6 transition"
                    >
                        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition" />
                        Back to Marketplace
                    </button>

                    {/* Machine Header */}
                    <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-100">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50">
                                        <Cpu className="h-6 w-6" />
                                    </span>
                                    <div>
                                        <p className="text-xs font-mono text-indigo-500">{machine.id}</p>
                                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                                            {machine.name}
                                        </h1>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 mt-2">
                                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${statusColors[machine.status]}`}>
                                        <StatusIcon className="h-3 w-3" />
                                        {machine.status}
                                    </span>
                                    <span className="text-sm text-slate-400">•</span>
                                    <span className="text-sm text-slate-600">{machine.category}</span>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <button className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
                                    <Heart className="h-5 w-5 text-slate-400 hover:text-red-500 transition" />
                                </button>
                                <button className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
                                    <Share2 className="h-5 w-5 text-slate-400" />
                                </button>
                                <button className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition">
                                    <Download className="h-5 w-5 text-slate-400" />
                                </button>
                            </div>
                        </div>

                        {/* Rating & Rate */}
                        <div className="flex flex-wrap items-center gap-6 mt-4 pt-4 border-t border-slate-100">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className={`h-4 w-4 ${i < Math.floor(machine.rating) ? "text-yellow-400 fill-yellow-400" : "text-slate-300"}`} />
                                    ))}
                                </div>
                                <span className="font-semibold text-slate-900">{machine.rating}</span>
                                <span className="text-sm text-slate-500">({machine.reviews} reviews)</span>
                            </div>
                            <span className="text-slate-300">|</span>
                            <div>
                                <span className="text-3xl font-bold text-indigo-600">₹{machine.rate.toLocaleString("en-IN")}</span>
                                <span className="text-sm text-slate-500 ml-1">/ hour</span>
                            </div>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                        {/* Left Column - Description & Specs */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Description */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                    <Info className="h-5 w-5 text-indigo-500" />
                                    Description
                                </h2>
                                <p className="text-slate-600 leading-relaxed">{machine.description}</p>
                            </div>

                            {/* Specifications */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                                    <Cpu className="h-5 w-5 text-indigo-500" />
                                    Specifications
                                </h2>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {machine.specs.map((spec, index) => (
                                        <li key={index} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                                            <span className="text-sm text-slate-700">{spec}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Features */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-indigo-500" />
                                    Key Features
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    {machine.features.map((feature, index) => (
                                        <span key={index} className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-xl text-sm font-medium border border-indigo-100">
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Booking Card */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl p-6 shadow-xl border border-slate-100 sticky top-6">
                                <h3 className="text-lg font-bold text-slate-900 mb-4">Book This Machine</h3>
                                
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                                        <span className="text-slate-600">Rate</span>
                                        <span className="font-bold text-slate-900">₹{machine.rate.toLocaleString("en-IN")}/hr</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                                        <span className="text-slate-600">Centre</span>
                                        <span className="font-medium text-slate-800">{machine.centre}</span>
                                    </div>
                                    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                                        <span className="text-slate-600">Make</span>
                                        <span className="font-medium text-slate-800">{machine.make}</span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t border-slate-100">
                                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
                                        <div className="flex -space-x-2">
                                            <div className="w-6 h-6 rounded-full bg-indigo-200 border-2 border-white flex items-center justify-center text-xs font-bold">A</div>
                                            <div className="w-6 h-6 rounded-full bg-blue-200 border-2 border-white flex items-center justify-center text-xs font-bold">B</div>
                                            <div className="w-6 h-6 rounded-full bg-purple-200 border-2 border-white flex items-center justify-center text-xs font-bold">C</div>
                                        </div>
                                        <span>Available for booking</span>
                                    </div>
                                    <button 
                                        onClick={() => alert(`Booking initiated for ${machine.name}`)}
                                        className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-indigo-200/50 hover:shadow-indigo-300/70 transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                                    >
                                        <Calendar className="h-5 w-5" />
                                        Book Now
                                    </button>
                                </div>
                            </div>

                            {/* Trust badges */}
                            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <Award className="h-5 w-5 text-indigo-600" />
                                    <span className="font-semibold text-slate-900">Premium Trust</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-600">
                                    <div className="flex items-center gap-1">
                                        <Shield className="h-3 w-3 text-emerald-500" />
                                        <span>Verified</span>
                                    </div>
                                    <span>•</span>
                                    <div className="flex items-center gap-1">
                                        <Users className="h-3 w-3 text-blue-500" />
                                        <span>2,000+ bookings</span>
                                    </div>
                                    <span>•</span>
                                    <div className="flex items-center gap-1">
                                        <Star className="h-3 w-3 text-yellow-500" />
                                        <span>4.8 avg rating</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}